import { useEffect, useState } from "react";
import { AvField, AvForm } from "availity-reactstrap-validation";
import { Button, Modal, ModalBody, ModalHeader, Spinner } from "reactstrap";
import {
    EVIDENCE_SOURCE_OPTIONS,
    buildAddEvidencePayload,
    getEvidenceSourceLabel,
} from "../helpers/measureDetail";
import {
    useAddPositiveListEvidence,
    useRemovePositiveListEvidence,
} from "../hooks/usePositiveListEvidence";
import useSoftwareDocuments from "../hooks/useSoftwareDocuments";

const evidenceDate = (item) => (
    item?.documented_at
    || item?.document_date
    || item?.created_at
    || item?.createdAt
    || ""
);

const formatEvidenceDate = (value) => {
    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return "";
    }

    return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
};

const EvidenceSection = ({
    softwareId,
    measureId,
    evidence = [],
    onModalChange,
    t,
}) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [formKey, setFormKey] = useState(0);
    const documentsQuery = useSoftwareDocuments({
        softwareId,
        enabled: isModalOpen,
    });
    const documents = Array.isArray(documentsQuery.data) ? documentsQuery.data : [];
    const addMutation = useAddPositiveListEvidence({
        softwareId,
        t,
        onSuccess: () => {
            setFormKey((key) => key + 1);
            setIsModalOpen(false);
        },
    });
    const removeMutation = useRemovePositiveListEvidence({
        softwareId,
        measureId,
        t,
    });
    const showDate = evidence.some((item) => evidenceDate(item));

    useEffect(() => {
        onModalChange?.(isModalOpen);
    }, [isModalOpen, onModalChange]);

    useEffect(() => () => onModalChange?.(false), [onModalChange]);

    const handleSubmit = (_event, values) => {
        addMutation.mutate({
            measureId,
            payload: buildAddEvidencePayload(values),
        });
    };

    return (
        <section className="positive-list-detail__card">
            <div className="positive-list-detail__card-header">
                <div className="d-flex align-items-center gap-2">
                    <h6 className="mb-0">{t("Evidence")}</h6>
                    <span className="positive-list-detail__count">{evidence.length}</span>
                </div>
                <Button
                    type="button"
                    color="primary"
                    outline
                    className="positive-list-detail__add"
                    onClick={() => setIsModalOpen(true)}
                >
                    <i className="ri-add-line align-middle me-1" />
                    {t("Add evidence")}
                </Button>
            </div>

            {evidence.length === 0 ? (
                <p className="positive-list-detail__text mb-0">
                    {t("No evidence has been added.")}
                </p>
            ) : (
                <div className="table-responsive">
                    <table className="positive-list-detail__table">
                        <thead>
                            <tr>
                                <th>{t("Source")}</th>
                                <th>{t("Citation")}</th>
                                <th>{t("Document")}</th>
                                {showDate ? <th>{t("Date")}</th> : null}
                                <th>{t("URL")}</th>
                                <th>{t("Action")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {evidence.map((item) => {
                                const isRemoving = removeMutation.isLoading
                                    && removeMutation.variables === item.id;
                                const dateLabel = formatEvidenceDate(evidenceDate(item));

                                return (
                                    <tr key={item.id}>
                                        <td>{t(getEvidenceSourceLabel(item.source_type))}</td>
                                        <td>{item.description || "—"}</td>
                                        <td>{item.attachment_title || "—"}</td>
                                        {showDate ? <td>{dateLabel || "—"}</td> : null}
                                        <td>
                                            {item.reference_url ? (
                                                <a
                                                    href={item.reference_url}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                >
                                                    {item.reference_url}
                                                </a>
                                            ) : "—"}
                                        </td>
                                        <td>
                                            <button
                                                type="button"
                                                className="positive-list-detail__icon-button"
                                                aria-label={t("Delete")}
                                                disabled={isRemoving}
                                                onClick={() => removeMutation.mutate(item.id)}
                                            >
                                                {isRemoving ? (
                                                    <Spinner size="sm" />
                                                ) : (
                                                    <i className="ri-delete-bin-line" />
                                                )}
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            <Modal
                isOpen={isModalOpen}
                toggle={() => setIsModalOpen(false)}
                centered
            >
                <ModalHeader toggle={() => setIsModalOpen(false)}>
                    {t("Add evidence")}
                </ModalHeader>
                <ModalBody>
                    <p className="text-muted">
                        {t("Add a source and citation that support this measure.")}
                    </p>
                    {isModalOpen && (
                        <AvForm
                            key={formKey}
                            className="needs-validation positive-list-form"
                            onValidSubmit={handleSubmit}
                        >
                            <AvField
                                type="select"
                                name="sourceType"
                                label={t("Source")}
                                value=""
                                errorMessage={t("Source is required.")}
                                validate={{
                                    required: {
                                        value: true,
                                        errorMessage: t("Source is required."),
                                    },
                                }}
                            >
                                <option value="">{t("Select")}</option>
                                {EVIDENCE_SOURCE_OPTIONS.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {t(option.labelKey)}
                                    </option>
                                ))}
                            </AvField>

                            <AvField
                                type="textarea"
                                name="description"
                                label={t("Citation")}
                                rows="3"
                                value=""
                                errorMessage={t("Citation is required.")}
                                validate={{
                                    required: {
                                        value: true,
                                        errorMessage: t("Citation is required."),
                                    },
                                }}
                            />

                            <AvField
                                name="referenceUrl"
                                label={t("Reference URL")}
                                value=""
                            />

                            <AvField
                                type="select"
                                name="softwareAttachmentId"
                                label={t("Document")}
                                value=""
                            >
                                <option value="">{t("Not set")}</option>
                                {documents.map((document) => (
                                    <option key={document.id} value={document.id}>
                                        {document.title || document.origin_name}
                                    </option>
                                ))}
                            </AvField>

                            <div className="d-flex justify-content-end gap-2">
                                <Button
                                    type="button"
                                    color="secondary"
                                    outline
                                    disabled={addMutation.isLoading}
                                    onClick={() => setIsModalOpen(false)}
                                >
                                    {t("Cancel")}
                                </Button>
                                <Button
                                    type="submit"
                                    color="primary"
                                    disabled={addMutation.isLoading}
                                >
                                    {addMutation.isLoading && (
                                        <Spinner size="sm" className="me-1" />
                                    )}
                                    {t("Save")}
                                </Button>
                            </div>
                        </AvForm>
                    )}
                </ModalBody>
            </Modal>
        </section>
    );
};

export default EvidenceSection;
